import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, RotateCcw, Share2, AlertOctagon, Users, Mail, Check } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import { QUESTIONS, ANSWERS, scoreToTier } from "@/data/selftest";
import Navbar from "@/components/sections/Navbar";
import Footer from "@/components/sections/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- Lead-magnet ----------
function LeadMagnet({ copy, score, tier }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | submitting | done | skipped
  const [skipped, setSkipped] = useState(false);

  if (state === "done") {
    return (
      <div
        data-testid="selftest-lead-done"
        className="mt-8 border border-redflag bg-[#160708] p-5 flex items-center gap-3 max-w-2xl"
      >
        <Check size={20} className="text-redflag" />
        <div className="text-sm text-white/85">{copy.leadDone}</div>
      </div>
    );
  }

  if (skipped) return null;

  const onSkip = async () => {
    // Best-effort anonymous stats POST with 3-attempt exponential backoff
    const delays = [0, 800, 2400]; // ms
    for (let i = 0; i < delays.length; i++) {
      if (delays[i]) await new Promise((r) => setTimeout(r, delays[i]));
      try {
        await axios.post(`${API}/selftest/results`, { score, tier });
        break;
      } catch (err) {
        // Stop retrying on definitive client errors (4xx) except 429
        const code = err?.response?.status;
        if (code && code >= 400 && code < 500 && code !== 429) break;
        // otherwise retry
      }
    }
    setSkipped(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (state === "submitting") return;
    setState("submitting");
    try {
      // Single POST creates the result row AND the newsletter sub
      await axios.post(`${API}/selftest/results`, {
        score,
        tier,
        email: email.trim(),
      });
      setState("done");
    } catch (err) {
      console.error(err);
      toast.error(copy.leadErr);
      setState("idle");
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid="selftest-lead-form"
      className="mt-8 border border-white/15 bg-[#0F0F0F] p-6 max-w-2xl"
    >
      <div className="flex items-start gap-4">
        <Mail size={22} className="text-redflag shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="overline">{copy.eyebrow}</div>
          <h3 className="mt-1 font-display uppercase text-xl text-white">
            {copy.leadTitle}
          </h3>
          <p className="mt-2 text-sm text-white/65 leading-relaxed">
            {copy.leadBody}
          </p>
          <div className="mt-5 flex flex-col sm:flex-row gap-2">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={copy.leadEmail}
              data-testid="selftest-lead-input"
              className="flex-1 bg-[#0A0A0A] border border-white/15 focus:border-redflag px-4 py-3 text-sm outline-none text-white placeholder:text-white/30"
            />
            <button
              type="submit"
              disabled={state === "submitting"}
              data-testid="selftest-lead-submit"
              className="btn-primary inline-flex items-center justify-center gap-2 px-5 py-3 text-xs uppercase tracking-wide font-medium disabled:opacity-60"
            >
              {state === "submitting" ? "…" : copy.leadSubmit}
            </button>
          </div>
          <button
            type="button"
            onClick={onSkip}
            data-testid="selftest-lead-skip"
            className="mt-3 text-xs uppercase tracking-widest font-mono-plex text-white/40 hover:text-white"
          >
            {copy.leadSkip} →
          </button>
        </div>
      </div>
    </form>
  );
}

const SELFTEST_COPY = {
  de: {
    eyebrow: "Selbsttest",
    title: "Wie viele Red Flags sind aktuell in deinem Leben?",
    intro:
      "12 ehrliche Fragen. Beantworte sie für dich — nicht für die Person, die du sein willst. Die Auswertung erscheint sofort und bleibt anonym.",
    start: "Test starten",
    back: "Zurück",
    next: "Weiter",
    finish: "Auswertung anzeigen",
    of: "von",
    yourScore: "Dein Wert",
    of36: "von 36",
    breakdown: "Auswertung nach Lebensbereichen",
    restart: "Test erneut machen",
    share: "Ergebnis teilen",
    sat: "SAT besuchen",
    socialProof: "Andere Teilnehmer",
    socialProofFmt: (s) =>
      `${s.above_9_pct}% der ${s.total} Teilnehmer:innen hatten 9 oder mehr Red Flags. Du bist nicht allein.`,
    socialProofEmpty: "Du bist eine:r der ersten — danke fürs Hinsehen.",
    leadTitle: "Speichere dein Ergebnis.",
    leadBody:
      "Sonntags eine Mail mit den wichtigsten Red Flags der Woche. Kein Spam, jederzeit abbestellbar.",
    leadEmail: "deine@email.ch",
    leadSubmit: "Speichern & abonnieren",
    leadSkip: "Überspringen",
    leadDone: "Eingetragen. Check dein Postfach.",
    leadErr: "Konnte nicht abonnieren. Versuch es bitte später.",
  },
  en: {
    eyebrow: "Self-test",
    title: "How many red flags are currently in your life?",
    intro:
      "12 honest questions. Answer them for yourself — not for the person you'd like to be. The result appears immediately and stays anonymous.",
    start: "Start the test",
    back: "Back",
    next: "Next",
    finish: "Show my result",
    of: "of",
    yourScore: "Your score",
    of36: "of 36",
    breakdown: "Breakdown by area",
    restart: "Retake the test",
    share: "Share result",
    sat: "Visit SAT",
    socialProof: "Other participants",
    socialProofFmt: (s) =>
      `${s.above_9_pct}% of ${s.total} participants had 9 or more red flags. You're not alone.`,
    socialProofEmpty: "You're one of the first — thank you for looking.",
    leadTitle: "Save your result.",
    leadBody:
      "One Sunday email with the most important red flags of the week. No spam, unsubscribe anytime.",
    leadEmail: "you@email.com",
    leadSubmit: "Save & subscribe",
    leadSkip: "Skip",
    leadDone: "Subscribed. Check your inbox.",
    leadErr: "Could not subscribe. Please try again later.",
  },
};

export default function SelfTest() {
  const { lang } = useLanguage();
  const copy = SELFTEST_COPY[lang];
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});

  const total = useMemo(
    () => Object.values(answers).reduce((s, v) => s + (v ?? 0), 0),
    [answers]
  );
  const done = Object.keys(answers).length === QUESTIONS.length;
  const tier = useMemo(() => scoreToTier(total), [total]);

  // Aggregate stats fetched once + after submission
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!done) return;
    // Dedup: do NOT auto-POST on completion. Only the LeadMagnet
    // posts (with or without email) so each user contributes
    // exactly one row to selftest_results.
    (async () => {
      try {
        const res = await axios.get(`${API}/selftest/stats`);
        setStats(res.data);
      } catch (err) {
        // Stats are a nice-to-have for social proof; failure shouldn't disrupt the user.
        console.debug("selftest stats fetch failed", err);
      }
    })();
  }, [done]);

  // Score per category
  const byCategory = useMemo(() => {
    const map = {};
    QUESTIONS.forEach((q) => {
      const v = answers[q.id];
      if (v == null) return;
      map[q.category] = map[q.category] || { total: 0, count: 0, max: 0 };
      map[q.category].total += v;
      map[q.category].count += 1;
      map[q.category].max += 3;
    });
    return Object.entries(map);
  }, [answers]);

  const q = QUESTIONS[step];
  const a = answers[q?.id];
  const lastStep = step === QUESTIONS.length - 1;

  const setAnswer = (val) => setAnswers((s) => ({ ...s, [q.id]: val }));

  const onShare = async () => {
    const text =
      lang === "de"
        ? `Ich habe ${total} von 36 Red Flags im RED FLAG Selbsttest. Wie viele hast du? → red-flag.ch`
        : `I scored ${total} of 36 red flags on the RED FLAG self-test. How many do you have? → red-flag.ch`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "RED FLAG", text });
        return;
      } catch (err) {
        // User cancelled native share or share API failed; fall through to clipboard.
        console.debug("native share unavailable", err);
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      // eslint-disable-next-line no-undef
      const { toast } = await import("sonner");
      toast.success(lang === "de" ? "In Zwischenablage kopiert." : "Copied to clipboard.");
    } catch (err) {
      console.debug("clipboard write failed", err);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <main
        data-testid="selftest-page"
        className="pt-28 md:pt-32 pb-24 relative noise-overlay"
      >
        <div
          aria-hidden="true"
          className="hero-mark right-[-4%] top-12 text-[60vh] hidden md:block"
        >
          ?
        </div>

        <div className="max-w-3xl mx-auto px-5 lg:px-8 relative">
          {!started && !done && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-white/55 hover:text-redflag transition-colors font-mono-plex uppercase tracking-widest"
              >
                <ArrowLeft size={14} /> Home
              </Link>
              <p className="overline mt-8">{copy.eyebrow}</p>
              <h1 className="font-display uppercase mt-4 text-4xl md:text-6xl leading-[0.95] tracking-tight">
                {copy.title}
              </h1>
              <p className="mt-6 text-base md:text-lg text-white/65 leading-relaxed max-w-2xl">
                {copy.intro}
              </p>
              <button
                data-testid="selftest-start"
                onClick={() => setStarted(true)}
                className="btn-primary mt-10 inline-flex items-center gap-3 px-7 py-4 text-sm uppercase tracking-wide font-medium"
              >
                <AlertOctagon size={18} />
                {copy.start}
              </button>
              <div className="mt-12 grid grid-cols-3 gap-px bg-white/10 border border-white/10">
                {[
                  { label: lang === "de" ? "Fragen" : "Questions", value: "12" },
                  { label: lang === "de" ? "Minuten" : "Minutes", value: "≈ 4" },
                  { label: lang === "de" ? "Anonym" : "Anonymous", value: "100%" },
                ].map((s) => (
                  <div key={s.label} className="bg-[#0F0F0F] p-5">
                    <div className="font-display text-3xl text-white">
                      {s.value}
                    </div>
                    <div className="overline mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {started && !done && (
            <div data-testid="selftest-question-flow">
              {/* progress */}
              <div className="flex items-center justify-between mb-8 font-mono-plex text-xs uppercase tracking-widest text-white/45">
                <span>
                  {step + 1} {copy.of} {QUESTIONS.length}
                </span>
                <span className="text-redflag">{q.category}</span>
              </div>
              <div className="h-[2px] bg-white/10 w-full mb-12">
                <div
                  className="h-full bg-redflag transition-all duration-500"
                  style={{
                    width: `${((step + 1) / QUESTIONS.length) * 100}%`,
                  }}
                />
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={q.id}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -14 }}
                  transition={{ duration: 0.35 }}
                >
                  <h2
                    data-testid={`selftest-question-${step}`}
                    className="font-display uppercase text-2xl md:text-4xl leading-tight tracking-tight"
                  >
                    {q[lang].q}
                  </h2>
                  <div className="mt-10 space-y-3">
                    {ANSWERS[lang].map((opt) => {
                      const selected = a === opt.value;
                      return (
                        <button
                          key={opt.value}
                          data-testid={`selftest-answer-${opt.value}`}
                          onClick={() => setAnswer(opt.value)}
                          className={`w-full text-left px-5 py-4 border transition-all flex items-center justify-between ${
                            selected
                              ? "border-redflag bg-[#1A0809] text-white"
                              : "border-white/12 bg-[#0F0F0F] text-white/85 hover:border-white/30"
                          }`}
                        >
                          <span className="text-base md:text-lg">{opt.label}</span>
                          <span
                            className={`font-mono-plex text-xs ${selected ? "text-redflag" : "text-white/35"}`}
                          >
                            +{opt.value}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              </AnimatePresence>

              <div className="mt-10 flex items-center justify-between">
                <button
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  disabled={step === 0}
                  data-testid="selftest-prev"
                  className="btn-ghost inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wide disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ArrowLeft size={14} /> {copy.back}
                </button>
                <button
                  onClick={() => {
                    if (a == null) return;
                    if (lastStep) {
                      // done — answers state already complete; the `done` computed flag will switch UI
                      setStep(step); // no-op, ensures render
                    } else {
                      setStep((s) => Math.min(QUESTIONS.length - 1, s + 1));
                    }
                  }}
                  disabled={a == null}
                  data-testid={lastStep ? "selftest-finish" : "selftest-next"}
                  className="btn-primary inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-wide font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {lastStep ? copy.finish : copy.next} <ArrowRight size={14} />
                </button>
              </div>
            </div>
          )}

          {done && (
            <motion.div
              data-testid="selftest-result"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <p className="overline">{copy.eyebrow}</p>
              <div className="mt-4 inline-flex items-center gap-3 font-mono-plex text-xs uppercase tracking-widest bg-redflag text-white px-3 py-1.5">
                {tier[lang].label}
              </div>
              <h1
                data-testid="selftest-result-headline"
                className="font-display uppercase mt-6 text-4xl md:text-6xl leading-[0.95] tracking-tight"
              >
                {tier[lang].headline}
              </h1>

              <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
                <div className="bg-[#0F0F0F] p-7 md:col-span-1">
                  <div className="overline">{copy.yourScore}</div>
                  <div
                    data-testid="selftest-total"
                    className="font-display text-6xl text-redflag mt-3"
                  >
                    {total}
                  </div>
                  <div className="font-mono-plex text-xs text-white/50">
                    {copy.of36}
                  </div>
                </div>
                <div className="bg-[#0F0F0F] p-7 md:col-span-2">
                  <div className="overline mb-5">{copy.breakdown}</div>
                  <ul className="space-y-3">
                    {byCategory.map(([cat, v]) => (
                      <li key={cat} className="text-sm">
                        <div className="flex items-center justify-between text-white/85">
                          <span>{cat}</span>
                          <span className="font-mono-plex text-xs text-white/55">
                            {v.total}/{v.max}
                          </span>
                        </div>
                        <div className="h-1 bg-white/10 mt-2">
                          <div
                            className="h-full bg-redflag"
                            style={{
                              width: `${(v.total / v.max) * 100}%`,
                            }}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <p className="mt-10 text-base md:text-lg text-white/75 leading-relaxed max-w-2xl">
                {tier[lang].body}
              </p>

              {/* Social proof — aggregate stats */}
              {stats && (
                <div
                  data-testid="selftest-social-proof"
                  className="mt-8 border border-white/10 bg-[#0F0F0F] p-5 flex items-start gap-4 max-w-2xl"
                >
                  <Users size={20} className="text-redflag shrink-0 mt-0.5" />
                  <div>
                    <div className="overline">{copy.socialProof}</div>
                    <p className="mt-1.5 text-sm text-white/75 leading-relaxed">
                      {stats.total > 0
                        ? copy.socialProofFmt(stats)
                        : copy.socialProofEmpty}
                    </p>
                  </div>
                </div>
              )}

              {/* Lead-magnet card */}
              <LeadMagnet copy={copy} score={total} tier={tier.key} />

              <div className="mt-10 flex flex-wrap gap-3">
                <a
                  href="#contact"
                  data-testid="selftest-cta-tier"
                  className="btn-primary inline-flex items-center gap-2 px-6 py-3 text-sm uppercase tracking-wide font-medium"
                >
                  {tier[lang].cta} <ArrowRight size={14} />
                </a>
                <button
                  onClick={onShare}
                  data-testid="selftest-share"
                  className="btn-ghost inline-flex items-center gap-2 px-5 py-3 text-sm uppercase tracking-wide"
                >
                  <Share2 size={14} /> {copy.share}
                </button>
                <button
                  onClick={() => {
                    setAnswers({});
                    setStep(0);
                    setStarted(false);
                  }}
                  data-testid="selftest-restart"
                  className="btn-ghost inline-flex items-center gap-2 px-5 py-3 text-sm uppercase tracking-wide"
                >
                  <RotateCcw size={14} /> {copy.restart}
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
